<template>
  <v-card class="guest-auth" flat>
    <v-tabs v-model="mode" grow>
      <v-tab value="login">{{ $t("booking.log_in") }}</v-tab>
      <v-tab value="register">{{ $t("booking.sign_up") }}</v-tab>
    </v-tabs>

    <v-card-text>
      <v-form v-if="mode === 'login'" @submit.prevent="submitLogin">
        <v-text-field
          v-model="loginEmail"
          :label="$t('booking.email')"
          type="email"
          required
          density="comfortable"
        />
        <v-text-field
          v-model="loginPassword"
          :label="$t('booking.password')"
          type="password"
          required
          density="comfortable"
        />
        <v-btn :loading="loading" type="submit" color="primary" block>
          {{ $t("booking.log_in") }}
        </v-btn>
      </v-form>

      <v-form v-else @submit.prevent="submitRegister">
        <v-text-field v-model="firstName" :label="$t('booking.first_name')" required density="comfortable" />
        <v-text-field v-model="lastName" :label="$t('booking.last_name')" required density="comfortable" />
        <v-text-field v-model="registerEmail" :label="$t('booking.email')" type="email" required density="comfortable" />
        <v-text-field
          v-model="registerPassword"
          :label="$t('booking.password')"
          type="password"
          required
          density="comfortable"
          :hint="$t('booking.password_hint')"
          persistent-hint
        />
        <div class="guest-auth__strength">
          <div
            class="guest-auth__strength-bar"
            :class="`guest-auth__strength-bar--${passwordStrength.level}`"
            :style="{ width: `${passwordStrength.percent}%` }"
          />
        </div>
        <span class="guest-auth__strength-label">{{ passwordStrength.label }}</span>

        <v-btn :loading="loading" type="submit" color="primary" block class="mt-4">
          {{ $t("booking.create_account") }}
        </v-btn>
      </v-form>

      <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
        {{ errorMessage }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { loginGuest, registerGuest } from "../../services/public-booking-api.service";
import { useGuestStore } from "../../stores/guest.store";

const emit = defineEmits<{ authenticated: [] }>();

const { t } = useI18n();
const guestStore = useGuestStore();

const mode = ref<"login" | "register">("login");
const loading = ref(false);
const errorMessage = ref("");

const loginEmail = ref("");
const loginPassword = ref("");

const firstName = ref("");
const lastName = ref("");
const registerEmail = ref("");
const registerPassword = ref("");

const passwordStrength = computed(() => {
  const value = registerPassword.value;
  let score = 0;
  if (value.length >= 8) score++;
  if (/[a-z]/.test(value)) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/\d/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;

  const levels = [
    { level: "weak", label: t("booking.password_too_weak"), percent: 20 },
    { level: "weak", label: t("booking.password_weak"), percent: 40 },
    { level: "fair", label: t("booking.password_fair"), percent: 60 },
    { level: "good", label: t("booking.password_good"), percent: 80 },
    { level: "strong", label: t("booking.password_strong"), percent: 100 },
  ];

  return levels[Math.min(score, levels.length - 1)];
});

const extractError = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.errors?.[0]?.title || fallback;
  }
  return fallback;
};

const submitLogin = async () => {
  errorMessage.value = "";
  loading.value = true;
  try {
    const user = await loginGuest({ email: loginEmail.value, password: loginPassword.value });
    guestStore.setGuestUser(user);
    emit("authenticated");
  } catch (error) {
    errorMessage.value = extractError(error, t("booking.login_error"));
  } finally {
    loading.value = false;
  }
};

const submitRegister = async () => {
  errorMessage.value = "";
  loading.value = true;
  try {
    const user = await registerGuest({
      email: registerEmail.value,
      password: registerPassword.value,
      first_name: firstName.value,
      last_name: lastName.value,
    });
    guestStore.setGuestUser(user);
    emit("authenticated");
  } catch (error) {
    errorMessage.value = extractError(error, t("booking.register_error"));
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.guest-auth {
  max-width: 420px;
  margin: 0 auto;

  &__strength {
    height: 4px;
    background: #eee;
    border-radius: 2px;
    overflow: hidden;
    margin-top: 8px;
  }

  &__strength-bar {
    height: 100%;
    transition: width 0.2s ease;

    &--weak {
      background: #e53935;
    }

    &--fair {
      background: #fbc02d;
    }

    &--good {
      background: #7cb342;
    }

    &--strong {
      background: #2e7d32;
    }
  }

  &__strength-label {
    font-size: 12px;
    color: #777;
  }
}
</style>
